/**
 * Fórmulas de Declaración de Renta AT 2026 - Régimen 14D3 (Pro PYME)
 *
 * Todas las fórmulas son funciones puras que reciben el estado completo
 * del formulario (data) y retornan valores calculados.
 * Replican fielmente las fórmulas del Excel "plantilla 2026 14d3.xlsx".
 *
 * Convención: cada función recibe `data` que contiene todas las secciones
 * con los valores editables ya ingresados por el usuario.
 */

const round = (val) => Math.round(val || 0);
const sum = (...vals) => vals.reduce((a, b) => (a || 0) + (b || 0), 0);

// ============================================================================
// ANTECEDENTES - Fórmulas internas
// ============================================================================

const antecedentes = {
  // Capital social — suma dinámica sobre todos los socios (N socios)
  totalParticipacion: (d) => sum(...(d.antecedentes.socios || []).map(s => s.participacion || 0)),
  totalCapitalHistorico: (d) => sum(...(d.antecedentes.socios || []).map(s => s.capitalHistorico || 0)),
  totalCapitalReajustado: (d) => sum(...(d.antecedentes.socios || []).map(s => s.capitalReajustado || 0)),

  // Ingresos del giro
  ingresosGiroPercibido: (row) => (row.netoFactura || 0) - (row.noPercibido || 0),
  totalIngresosGiroNeto: (d) => sum(
    d.antecedentes.ventasNoRelacionadasNeto,
    d.antecedentes.ventasRelacionadasProPymeNeto,
    d.antecedentes.ventasRelacionadas14ANeto,
    d.antecedentes.cobroFactura2024Neto
  ),
  totalIngresosGiroNoPercibido: (d) => sum(
    d.antecedentes.ventasNoRelacionadasNoPercibido,
    d.antecedentes.ventasRelacionadasProPymeNoPercibido,
    d.antecedentes.ventasRelacionadas14ANoPercibido,
    d.antecedentes.cobroFactura2024NoPercibido
  ),
  totalIngresosGiroPercibido: (d) =>
    antecedentes.totalIngresosGiroNeto(d) - antecedentes.totalIngresosGiroNoPercibido(d),

  // Otros ingresos
  totalOtrosIngresos: (d) => sum(
    d.antecedentes.ingresosVentaComputador,
    d.antecedentes.creditoBanco
  ),

  // Dividendos
  creditoDividendo1: (d) => round(d.antecedentes.dividendo1Percibido * d.antecedentes.dividendo1Factor),
  creditoDividendo2: (d) => round(d.antecedentes.dividendo2Percibido * d.antecedentes.dividendo2Factor),
  creditoDividendo3: (d) => round(d.antecedentes.dividendo3Percibido * d.antecedentes.dividendo3Factor),
  totalDividendosCredito: (d) => sum(
    antecedentes.creditoDividendo1(d),
    antecedentes.creditoDividendo2(d),
    antecedentes.creditoDividendo3(d)
  ),
  totalDividendosPercibido: (d) => sum(
    d.antecedentes.dividendo1Percibido,
    d.antecedentes.dividendo2Percibido,
    d.antecedentes.dividendo3Percibido,
    d.antecedentes.dividendo4Percibido,
    d.antecedentes.dividendoINR
  ),

  // Renta fuente extranjera
  rentaExtranjera: (d) => d.creditoIPE.dividendoExtranjeroUSD,
  totalRentaExtranjera: (d) => d.creditoIPE.dividendoExtranjeroUSD,

  // Egresos
  honorariosPagado: (d) => (d.antecedentes.honorariosBruto || 0) - (d.antecedentes.honorariosAdeudado || 0),
  honorariosRetencion: (d) => round((d.antecedentes.honorariosBruto || 0) * 0.145),
  amortizacionTotal: (d) => sum(d.antecedentes.amortizacionCapital, d.antecedentes.amortizacionInteres),
  ppmPagados: (d) => d.antecedentes.ppmHistorico,
  // PPM actualizado = lo enterado durante el año + su reajuste al 31.12. Es el
  // monto que se imputa contra el IDPC en la liquidación anual (código 36), no
  // el histórico. El reajuste, además, es ingreso del ejercicio (código 1588).
  ppmActualizado: (d) => sum(d.antecedentes.ppmHistorico, d.antecedentes.reajustePPMHistorico),
  retirosTotales: (d) => sum(...(d.antecedentes.retiros || []).map(r => r.retiroHistorico)),
  remuneracionesPagado: (d) => (d.antecedentes.remuneracionesBruto || 0) - (d.antecedentes.remuneracionesAdeudado || 0),
  totalEgresosNeto: (d) => sum(d.antecedentes.comprasNeto, d.antecedentes.compraCamionetaNeto),
  totalEgresosAdeudado: (d) => sum(
    d.antecedentes.remuneracionesAdeudado,
    antecedentes.honorariosRetencion(d)
  ),
  totalEgresosPagado: (d) => sum(
    d.antecedentes.comprasPagado,
    d.antecedentes.compraCamionetaPagado,
    antecedentes.remuneracionesPagado(d),
    antecedentes.honorariosPagado(d),
    d.antecedentes.gastosGenerales,
    d.antecedentes.arriendos,
    d.antecedentes.cotizacionesDic2024,
    d.antecedentes.retencionesIUSCDic2024,
    d.antecedentes.pagoProveedores2024,
    antecedentes.amortizacionTotal(d),
    d.antecedentes.pagoInteresesMultas,
    d.antecedentes.pagoIDPCAT2025,
    d.antecedentes.ppmHistorico,
    antecedentes.retirosTotales(d)
  ),

  // Retiros actualizados (datos desde módulo Socios y Dividendos)
  totalRetirosHistorico: (d) => sum(...(d.antecedentes.retiros || []).map(r => r.retiroHistorico)),
  totalRetirosActualizado: (d) => sum(...(d.antecedentes.retiros || []).map(r => r.retiroActualizado)),

  // Factor IDPC
  factorIDPC: (d) => d.antecedentes.tasaIDPC / (1 - d.antecedentes.tasaIDPC),

  // Capital aportado código 1374 — aplica solo si no hay CPTS del año anterior (empresa nueva)
  capitalAportado1374: (d) => d.antecedentes.cptsAl31Dic2024 ? 0 : antecedentes.totalCapitalHistorico(d),

  // Factor crédito acumulado al 31.12.2016
  factorCredito2016: (d) => {
    const stut = d.antecedentes.remanentesAnteriores?.STUT || 0;
    if (stut === 0) return 0;
    return Math.trunc((d.antecedentes.remanentesAnteriores?.creditoIDPC2016ConDev || 0) / stut * 1000000) / 1000000;
  },
};

// ============================================================================
// CRÉDITO IPE - Impuesto Pagado en el Exterior
// ============================================================================

const creditoIPE = {
  // (a) Renta Neta
  dividendoEnPesos: (d) => round(d.creditoIPE.dividendoExtranjeroUSD * d.creditoIPE.tipoCambioDolar),
  rentaNeta: (d) => {
    const divPesos = creditoIPE.dividendoEnPesos(d);
    return sum(divPesos, -d.creditoIPE.perdidasRelacionadas, -d.creditoIPE.gastosRelacionados);
  },

  // (b) Tope Individual
  impuestoRetencionExtranjero: (d) => {
    const divPesos = creditoIPE.dividendoEnPesos(d);
    if (divPesos === 0) return 0;
    return round(divPesos / (1 - d.creditoIPE.tasaRetencion) * d.creditoIPE.tasaRetencion);
  },
  impuestoCorporativoExtranjero: (d) => {
    const divPesos = creditoIPE.dividendoEnPesos(d);
    if (divPesos === 0) return 0;
    const base = divPesos / (1 - d.creditoIPE.tasaRetencion);
    return round(base / (1 - d.creditoIPE.tasaCorporativo) * d.creditoIPE.tasaCorporativo);
  },
  totalImpuestosExtranjeros: (d) => sum(
    creditoIPE.impuestoRetencionExtranjero(d),
    creditoIPE.impuestoCorporativoExtranjero(d)
  ),
  tope35RentaBruta: (d) => {
    const divPesos = creditoIPE.dividendoEnPesos(d);
    const impuestos = creditoIPE.totalImpuestosExtranjeros(d);
    return round((divPesos + impuestos) * 0.35);
  },
  topeIndividual: (d) => Math.min(
    creditoIPE.totalImpuestosExtranjeros(d),
    creditoIPE.tope35RentaBruta(d)
  ),

  // (c) Tope Global
  sumaRentaNetatopeInd: (d) => sum(creditoIPE.rentaNeta(d), creditoIPE.topeIndividual(d)),
  topeGlobal: (d) => round(creditoIPE.sumaRentaNetatopeInd(d) * 0.35),

  // (d) CTD
  ctd: (d) => Math.min(creditoIPE.topeIndividual(d), creditoIPE.topeGlobal(d)),

  // (e) Renta Imponible
  rentaImponible: (d) => sum(creditoIPE.rentaNeta(d), creditoIPE.ctd(d)),

  // Imputación del CTD
  creditoIPEContraIDPC: (d) => round(creditoIPE.rentaImponible(d) * d.antecedentes.tasaIDPC),
  creditoIPEContraIF: (d) => creditoIPE.ctd(d) - creditoIPE.creditoIPEContraIDPC(d),
};

// ============================================================================
// BASE IMPONIBLE Y ART. 14 LETRA E)
// ============================================================================

const baseImponible = {
  // Ingresos
  ventasNoRelacionadas: (d) => d.antecedentes.ventasNoRelacionadasPercibido || d.antecedentes.ventasNoRelacionadasNeto,
  ventasRelacionadas14A: (d) => d.antecedentes.ventasRelacionadas14APercibido || d.antecedentes.ventasRelacionadas14ANeto,
  ingresosDevengadosPercibidos: (d) => (d.antecedentes.cobroFactura2024Neto || 0) - (d.antecedentes.cobroFactura2024NoPercibido || 0),
  totalIngresosGiro: (d) => sum(
    baseImponible.ventasNoRelacionadas(d),
    baseImponible.ventasRelacionadas14A(d),
    baseImponible.ingresosDevengadosPercibidos(d)
  ),

  dividendoFuenteExtranjera: (d) => creditoIPE.rentaNeta(d),
  ctdDeterminado: (d) => creditoIPE.ctd(d),
  totalIngresosFuenteExtranjera: (d) => sum(
    baseImponible.dividendoFuenteExtranjera(d),
    baseImponible.ctdDeterminado(d)
  ),

  ingresosVentaComputador: (d) => d.antecedentes.ingresosVentaComputador,
  reajustePPM: (d) => d.antecedentes.reajustePPMHistorico || 0,
  totalOtrosIngresos: (d) => sum(
    baseImponible.ingresosVentaComputador(d),
    baseImponible.reajustePPM(d)
  ),

  totalIngresos: (d) => sum(
    baseImponible.totalIngresosGiro(d),
    baseImponible.totalIngresosFuenteExtranjera(d),
    baseImponible.totalOtrosIngresos(d)
  ),

  // Egresos
  comprasNetas: (d) => -(d.antecedentes.comprasPagado || d.antecedentes.comprasNeto),
  gastosAdeudados2024Pagados: (d) => -(d.antecedentes.pagoProveedores2024 || 0),
  compraCamioneta: (d) => -(d.antecedentes.compraCamionetaPagado || d.antecedentes.compraCamionetaNeto),
  remuneraciones: (d) => -(
    antecedentes.remuneracionesPagado(d) +
    d.antecedentes.cotizacionesDic2024 +
    d.antecedentes.retencionesIUSCDic2024
  ),
  honorarios: (d) => -(d.antecedentes.honorariosBruto || 0),
  gastosGenerales: (d) => -(d.antecedentes.gastosGenerales),
  arriendos: (d) => -(d.antecedentes.arriendos),
  interesesPrestamo: (d) => -(d.antecedentes.amortizacionInteres),
  pagoInteresesMultas: (d) => -(d.antecedentes.pagoInteresesMultas),
  pagoIDPC: (d) => -(d.antecedentes.pagoIDPCAT2025),

  totalEgresosGiro: (d) => sum(
    baseImponible.comprasNetas(d),
    baseImponible.gastosAdeudados2024Pagados(d),
    baseImponible.compraCamioneta(d),
    baseImponible.remuneraciones(d),
    baseImponible.honorarios(d),
    baseImponible.gastosGenerales(d),
    baseImponible.arriendos(d),
    baseImponible.interesesPrestamo(d),
    baseImponible.pagoInteresesMultas(d),
    baseImponible.pagoIDPC(d)
  ),

  totalEgresosAnuales: (d) => baseImponible.totalEgresosGiro(d),

  // Gastos rechazados
  gastosRechazadosIntereses: (d) => -(baseImponible.pagoInteresesMultas(d)),
  gastosRechazadosIDPC: (d) => -(baseImponible.pagoIDPC(d)),
  totalGastosRechazados: (d) => sum(
    baseImponible.gastosRechazadosIntereses(d),
    baseImponible.gastosRechazadosIDPC(d)
  ),

  // BI antes de incentivo
  baseImponibleAntesIncentivo: (d) => sum(
    baseImponible.totalIngresos(d),
    baseImponible.totalEgresosAnuales(d),
    baseImponible.totalGastosRechazados(d)
  ),

  // Incentivo al ahorro art. 14 letra E)
  biInvertida: (d) => {
    const bi = baseImponible.baseImponibleAntesIncentivo(d);
    const retiros = antecedentes.totalRetirosHistorico(d);
    const intereses = d.antecedentes.pagoInteresesMultas;
    const idpc = d.antecedentes.pagoIDPCAT2025;
    return sum(bi, -retiros, -intereses, -idpc);
  },
  tope5000UF: (d) => 5000 * d.antecedentes.valorUF,
  incentivoAhorro: (d) => {
    const invertida = baseImponible.biInvertida(d);
    const mitad = round(0.5 * invertida);
    const tope = baseImponible.tope5000UF(d);
    return Math.min(tope, Math.max(mitad, 0));
  },

  // BASE IMPONIBLE FINAL
  baseImponibleIDPC: (d) => sum(
    baseImponible.baseImponibleAntesIncentivo(d),
    -baseImponible.incentivoAhorro(d)
  ),

  // IDPC
  idpc: (d) => round(baseImponible.baseImponibleIDPC(d) * d.antecedentes.tasaIDPC),

  // LIQUIDACIÓN ANUAL
  // Del IDPC determinado se descuentan los PPM actualizados (código 36) para
  // llegar al resultado de la liquidación anual (código 305): positivo =
  // impuesto a pagar, negativo = saldo a favor del contribuyente. Es el impuesto
  // real antes del reajuste del art. 72 LIR, que solo se conoce al presentar el
  // F-22 en abril y se calcula sobre el saldo a pagar.
  ppmActualizado: (d) => antecedentes.ppmActualizado(d),
  resultadoLiquidacionAnual: (d) => baseImponible.idpc(d) - baseImponible.ppmActualizado(d),

  // Saldo a favor (código 85): solo existe cuando la liquidación anual da
  // negativo, o sea cuando los PPM superaron al impuesto. Va sin el reajuste
  // del art. 72, que el formulario aplica únicamente al lado a pagar.
  saldoAFavor: (d) => Math.max(-baseImponible.resultadoLiquidacionAnual(d), 0),
  // Devolución solicitada (código 87) = código 85 menos el 86 (saldo puesto a
  // disposición de los socios). El 86 no se modela todavía, así que 87 = 85.
  devolucionSolicitada: (d) => baseImponible.saldoAFavor(d),
};

// ============================================================================
// CPT SIMPLIFICADO
// ============================================================================

const cptSimplificado = {
  // Si hay CPTS del año anterior (1545) se usa ese; si es empresa nueva se usa capital aportado (1374)
  cptInicial: (d) => sum(d.antecedentes.cptsAl31Dic2024, antecedentes.capitalAportado1374(d)),
  baseImponibleEjercicio: (d) => baseImponible.baseImponibleIDPC(d),
  dividendosPercibidos: (d) => d.antecedentes.totalDividendosPercibido || antecedentes.totalDividendosPercibido(d),
  retirosHistoricos: (d) => -(antecedentes.totalRetirosHistorico(d)),
  interesesMultas: (d) => -(d.antecedentes.pagoInteresesMultas),
  pagoIDPC: (d) => -(d.antecedentes.pagoIDPCAT2025),
  creditoIPEContraIF: (d) => -(creditoIPE.creditoIPEContraIF(d)),
  incentivoAhorro: (d) => baseImponible.incentivoAhorro(d),
  cptsPositivoFinal: (d) => sum(
    cptSimplificado.cptInicial(d),
    cptSimplificado.baseImponibleEjercicio(d),
    cptSimplificado.dividendosPercibidos(d),
    cptSimplificado.retirosHistoricos(d),
    cptSimplificado.interesesMultas(d),
    cptSimplificado.pagoIDPC(d),
    cptSimplificado.creditoIPEContraIF(d),
    cptSimplificado.incentivoAhorro(d)
  ),
};

// ============================================================================
// RAI FINAL
// ============================================================================

const raiFinal = {
  cptsPositivo: (d) => cptSimplificado.cptsPositivoFinal(d),
  retirosHistoricos: (d) => antecedentes.totalRetirosHistorico(d),
  // Calcula saldo REX sin pasar por subtotalAntes (evita ciclo raiFinal↔rtreFormulas)
  // REX_RAP y REX_INR solo dependen de remanentes anteriores + dividendoINR del ejercicio
  saldoREXPositivo: (d) => {
    const remREX_RAP = d.antecedentes.remanentesAnteriores?.REX_RAP || 0;
    const remREX_INR = d.antecedentes.remanentesAnteriores?.REX_INR || 0;
    const rexINR = d.antecedentes.dividendoINR || 0;
    return -(remREX_RAP + remREX_INR + rexINR);
  },
  capitalSocial: (d) => -(antecedentes.totalCapitalReajustado(d)),
  rai: (d) => sum(
    raiFinal.cptsPositivo(d),
    raiFinal.retirosHistoricos(d),
    raiFinal.saldoREXPositivo(d),
    raiFinal.capitalSocial(d)
  ),
};

// ============================================================================
// RTRE - Registro Tributario de Rentas Empresariales
// ============================================================================

const rtreFormulas = {
  // Remanente anterior (viene de data ingresada)
  remanente: (d) => ({
    control: sum(
      d.antecedentes.remanentesAnteriores?.RAI || 0,
      d.antecedentes.remanentesAnteriores?.REX_RAP || 0,
      d.antecedentes.remanentesAnteriores?.REX_INR || 0
    ),
    RAI: d.antecedentes.remanentesAnteriores?.RAI || 0,
    REX_RAP: d.antecedentes.remanentesAnteriores?.REX_RAP || 0,
    REX_INR: d.antecedentes.remanentesAnteriores?.REX_INR || 0,
    SAC_noSujetoSinDev: d.antecedentes.remanentesAnteriores?.SAC_noSujetoSinDev || 0,
    SAC_noSujetoConDev: d.antecedentes.remanentesAnteriores?.SAC_noSujetoConDev || 0,
    SAC_sujetoConDev: d.antecedentes.remanentesAnteriores?.SAC_sujetoConDev || 0,
    SAC_IPE_sinDev: d.antecedentes.remanentesAnteriores?.SAC_IPE_sinDev || 0,
    SAC_2016_conDev: d.antecedentes.remanentesAnteriores?.SAC_2016_conDev || 0,
    STUT: d.antecedentes.remanentesAnteriores?.STUT || 0,
  }),

  // Reverso RAI
  reversoRAI: (d) => {
    const rem = rtreFormulas.remanente(d);
    return { control: -rem.RAI, RAI: -rem.RAI };
  },

  // RAI del ejercicio
  raiEjercicio: (d) => {
    const rai = raiFinal.rai(d);
    return { control: rai, RAI: rai };
  },

  // REX percibidos
  rexPercibidos: (d) => ({
    control: d.antecedentes.dividendoINR || 0,
    REX_INR: d.antecedentes.dividendoINR || 0,
  }),

  // Crédito IDPC sobre RLI
  creditoIDPCSobreRLI: (d) => ({
    SAC_IPE_sinDev: creditoIPE.creditoIPEContraIDPC(d),
    SAC_noSujetoConDev: baseImponible.idpc(d) - creditoIPE.creditoIPEContraIDPC(d),
    SAC_IPE_IF: creditoIPE.creditoIPEContraIF(d),
  }),

  // Crédito IDPC sobre dividendo percibido
  creditoIDPCSobreDividendo: (d) => ({
    SAC_noSujetoConDev: d.antecedentes.dividendo2Credito || 0,
    SAC_sujetoConDev: d.antecedentes.dividendo1Credito || 0,
    SAC_2016_conDev: d.antecedentes.dividendo3Credito || 0,
    STUT: d.antecedentes.dividendoSTUT || 0,
  }),

  // Subtotal antes de imputaciones
  subtotalAntes: (d) => {
    const rem = rtreFormulas.remanente(d);
    const rev = rtreFormulas.reversoRAI(d);
    const rai = rtreFormulas.raiEjercicio(d);
    const rex = rtreFormulas.rexPercibidos(d);
    const cred = rtreFormulas.creditoIDPCSobreRLI(d);
    const credDiv = rtreFormulas.creditoIDPCSobreDividendo(d);
    return {
      control: sum(rem.control, rev.control, rai.control, rex.control),
      RAI: sum(rem.RAI, rev.RAI, rai.RAI),
      REX_RAP: rem.REX_RAP,
      REX_INR: sum(rem.REX_INR, rex.REX_INR),
      SAC_noSujetoSinDev: rem.SAC_noSujetoSinDev,
      SAC_noSujetoConDev: sum(rem.SAC_noSujetoConDev, cred.SAC_noSujetoConDev, credDiv.SAC_noSujetoConDev),
      SAC_sujetoConDev: sum(rem.SAC_sujetoConDev, credDiv.SAC_sujetoConDev),
      SAC_IPE_sinDev: sum(rem.SAC_IPE_sinDev, cred.SAC_IPE_sinDev),
      SAC_2016_conDev: sum(rem.SAC_2016_conDev, credDiv.SAC_2016_conDev),
      STUT: sum(rem.STUT, credDiv.STUT),
    };
  },

  // Factor para crédito no sujeto a restitución
  factorNoSujeto: (d) => {
    const sub = rtreFormulas.subtotalAntes(d);
    return sub.SAC_noSujetoConDev > 0 ? 0.142857 : 0;
  },

  // Calcula las proporciones globales RAI/REX sobre el total de retiros actualizados
  prorrateoRetiros: (d) => {
    const retiros = d.antecedentes.retiros || [];
    const sub = rtreFormulas.subtotalAntes(d);
    const raiDisponible = Math.max(sub.RAI || 0, 0);
    const retiroTotal = retiros.reduce((s, r) => s + (r.retiroActualizado || 0), 0);

    if (retiroTotal === 0) return { propRAI: 1, propREX: 0 };
    if (retiroTotal <= raiDisponible) return { propRAI: 1, propREX: 0 };

    const propRAI = raiDisponible / retiroTotal;
    return { propRAI, propREX: 1 - propRAI };
  },

  // Imputaciones individuales: un objeto por cada retiro, con RAI/REX prorrateados y créditos SAC
  imputacionesRetiros: (d) => {
    const retiros = d.antecedentes.retiros || [];
    const { propRAI } = rtreFormulas.prorrateoRetiros(d);
    const factor = 0.142857;

    return retiros.map((retiro) => {
      const monto = retiro.retiroActualizado || 0;
      const imputadoRAI = Math.round(monto * propRAI);
      const imputadoREX = monto - imputadoRAI;

      return {
        nombre: retiro.nombre || '',
        fechaRetiro: retiro.fechaRetiro || '',
        control: -monto,
        RAI: -imputadoRAI,
        REX_RAP: -imputadoREX,
        SAC_noSujetoConDev: -round(imputadoRAI * factor),
      };
    });
  },

  // Crédito sobre gastos rechazados
  creditoGastosRechazados: (d) => {
    const factor = 0.142857;
    return {
      SAC_noSujetoConDev: -round(d.antecedentes.pagoInteresesMultas * factor),
    };
  },

  // Remanente ejercicio siguiente
  remanenteSiguiente: (d) => {
    const sub = rtreFormulas.subtotalAntes(d);
    const imputaciones = rtreFormulas.imputacionesRetiros(d);

    let totalControl = 0, totalRAI = 0, totalREX_RAP = 0;
    let totalSAC_noSujetoConDev = 0, totalSAC_IPE = 0;

    imputaciones.forEach((imp) => {
      totalControl += imp.control || 0;
      totalRAI += imp.RAI || 0;
      totalREX_RAP += imp.REX_RAP || 0;
      totalSAC_noSujetoConDev += imp.SAC_noSujetoConDev || 0;
      totalSAC_IPE += imp.SAC_IPE || 0;
    });

    const cGR = rtreFormulas.creditoGastosRechazados(d);

    return {
      control: sum(sub.control, totalControl),
      RAI: sum(sub.RAI, totalRAI),
      REX_RAP: sum(sub.REX_RAP, totalREX_RAP),
      REX_INR: sub.REX_INR,
      SAC_noSujetoSinDev: sub.SAC_noSujetoSinDev,
      SAC_noSujetoConDev: sum(sub.SAC_noSujetoConDev, totalSAC_noSujetoConDev, cGR.SAC_noSujetoConDev),
      SAC_sujetoConDev: sub.SAC_sujetoConDev,
      SAC_IPE_sinDev: sum(sub.SAC_IPE_sinDev, totalSAC_IPE),
      SAC_2016_conDev: sub.SAC_2016_conDev,
      STUT: sub.STUT,
    };
  },
};

// ============================================================================
// RECUADROS R17 - R21  (resumen para F-22)
// ============================================================================

const r17 = {
  ingresosGiroPercibidos: (d) => baseImponible.ventasNoRelacionadas(d),
  ingresosDevengadosPercibidos: (d) => baseImponible.ingresosDevengadosPercibidos(d),
  rentasFuenteExtranjera: (d) => sum(baseImponible.dividendoFuenteExtranjera(d), baseImponible.ctdDeterminado(d)),
  ingresosRelacionados14A: (d) => baseImponible.ventasRelacionadas14A(d),
  otrosIngresosPercibidos: (d) => sum(baseImponible.ingresosVentaComputador(d), baseImponible.reajustePPM(d)),
  totalIngresos: (d) => baseImponible.totalIngresos(d),
  existenciasPagadas: (d) => Math.abs(baseImponible.comprasNetas(d)),
  gastosAdeudadosPagados: (d) => Math.abs(baseImponible.gastosAdeudados2024Pagados(d)),
  remuneracionesPagadas: (d) => Math.abs(baseImponible.remuneraciones(d)),
  honorariosPagados: (d) => Math.abs(baseImponible.honorarios(d)),
  activoFijoPagado: (d) => Math.abs(baseImponible.compraCamioneta(d)),
  arriendosPagados: (d) => Math.abs(baseImponible.arriendos(d)),
  interesesPagados: (d) => Math.abs(baseImponible.interesesPrestamo(d)),
  partidasArt21: (d) => sum(Math.abs(baseImponible.pagoInteresesMultas(d)), Math.abs(baseImponible.pagoIDPC(d))),
  otrosGastos: (d) => Math.abs(baseImponible.gastosGenerales(d)),
  totalEgresos: (d) => Math.abs(baseImponible.totalEgresosAnuales(d)),
  gastosRechazadosArt21: (d) => baseImponible.totalGastosRechazados(d),
  biAntesIncentivo: (d) => baseImponible.baseImponibleAntesIncentivo(d),
  incentivoAhorro: (d) => baseImponible.incentivoAhorro(d),
  biAfectaIDPC: (d) => baseImponible.baseImponibleIDPC(d),
};

const r18 = {
  cptsPositivo: (d) => cptSimplificado.cptsPositivoFinal(d),
  retirosHistoricos: (d) => antecedentes.totalRetirosHistorico(d),
  subtotal: (d) => sum(r18.cptsPositivo(d), r18.retirosHistoricos(d)),
  saldoREXPositivo: (d) => -raiFinal.saldoREXPositivo(d),
  capitalAportado: (d) => -raiFinal.capitalSocial(d),
  rai: (d) => raiFinal.rai(d),
};

const r19 = {
  cptInicial: (d) => d.antecedentes.cptsAl31Dic2024,          // código 1445 — CPTS año anterior (F-22 AT 2025)
  capitalAportado1374: (d) => antecedentes.capitalAportado1374(d), // código 1374 — empresa nueva
  biAfecta: (d) => cptSimplificado.baseImponibleEjercicio(d),
  dividendosPercibidos: (d) => Math.abs(cptSimplificado.dividendosPercibidos(d)),
  retiros: (d) => Math.abs(cptSimplificado.retirosHistoricos(d)),
  partidasArt21: (d) => sum(Math.abs(cptSimplificado.interesesMultas(d)), Math.abs(cptSimplificado.pagoIDPC(d))),
  creditoIPE: (d) => Math.abs(cptSimplificado.creditoIPEContraIF(d)),
  incentivoAhorro: (d) => cptSimplificado.incentivoAhorro(d),
  cptsPositivoFinal: (d) => cptSimplificado.cptsPositivoFinal(d),
};

const r20 = {
  // Valores del RTRE mapeados al recuadro R20
  remanente: (d) => rtreFormulas.remanente(d),
  reverso: (d) => rtreFormulas.reversoRAI(d),
  aumentoRAI: (d) => rtreFormulas.raiEjercicio(d),
  aumentoINR: (d) => rtreFormulas.rexPercibidos(d),
  retiros: (d) => {
    return rtreFormulas.imputacionesRetiros(d).reduce((s, imp) => s + (imp.RAI || 0), 0);
  },
  remanenteSiguiente: (d) => rtreFormulas.remanenteSiguiente(d),
};

const r21 = {
  // Valores del SAC mapeados al recuadro R21
  remanente: (d) => rtreFormulas.remanente(d),
  idpcBI: (d) => rtreFormulas.creditoIDPCSobreRLI(d),
  idpcDividendo: (d) => rtreFormulas.creditoIDPCSobreDividendo(d),
  asignadoRetiros: (d) => {
    return rtreFormulas.imputacionesRetiros(d).reduce(
      (total, imp) => ({
        SAC_noSujetoSinDev: total.SAC_noSujetoSinDev + (imp.SAC_noSujetoSinDev || 0),
        SAC_noSujetoConDev: total.SAC_noSujetoConDev + (imp.SAC_noSujetoConDev || 0),
        SAC_IPE: total.SAC_IPE + (imp.SAC_IPE || 0),
      }),
      { SAC_noSujetoSinDev: 0, SAC_noSujetoConDev: 0, SAC_IPE: 0 }
    );
  },
  gastosRechazados: (d) => rtreFormulas.creditoGastosRechazados(d),
  remanenteSiguiente: (d) => rtreFormulas.remanenteSiguiente(d),
};

// ============================================================================
// FUNCIÓN PRINCIPAL: recalcula todos los campos computados
// ============================================================================

const recalculate = (data) => {
  return {
    antecedentes: {
      totalCapitalHistorico: antecedentes.totalCapitalHistorico(data),
      totalCapitalReajustado: antecedentes.totalCapitalReajustado(data),
      capitalAportado1374: antecedentes.capitalAportado1374(data),
      totalIngresosGiroNeto: antecedentes.totalIngresosGiroNeto(data),
      totalIngresosGiroPercibido: antecedentes.totalIngresosGiroPercibido(data),
      totalOtrosIngresos: antecedentes.totalOtrosIngresos(data),
      totalDividendosPercibido: antecedentes.totalDividendosPercibido(data),
      totalDividendosCredito: antecedentes.totalDividendosCredito(data),
      remuneracionesPagado: antecedentes.remuneracionesPagado(data),
      honorariosPagado: antecedentes.honorariosPagado(data),
      totalEgresosPagado: antecedentes.totalEgresosPagado(data),
      totalRetirosHistorico: antecedentes.totalRetirosHistorico(data),
      totalRetirosActualizado: antecedentes.totalRetirosActualizado(data),
      totalPpmHistorico: data.antecedentes.ppmHistorico || 0,
      totalPpmActualizado: antecedentes.ppmActualizado(data),
      factorIDPC: antecedentes.factorIDPC(data),
    },
    creditoIPE: {
      dividendoEnPesos: creditoIPE.dividendoEnPesos(data),
      rentaNeta: creditoIPE.rentaNeta(data),
      topeIndividual: creditoIPE.topeIndividual(data),
      topeGlobal: creditoIPE.topeGlobal(data),
      ctd: creditoIPE.ctd(data),
      rentaImponible: creditoIPE.rentaImponible(data),
      creditoIPEContraIDPC: creditoIPE.creditoIPEContraIDPC(data),
      creditoIPEContraIF: creditoIPE.creditoIPEContraIF(data),
    },
    baseImponible: {
      totalIngresosGiro: baseImponible.totalIngresosGiro(data),
      totalIngresosFuenteExtranjera: baseImponible.totalIngresosFuenteExtranjera(data),
      totalOtrosIngresos: baseImponible.totalOtrosIngresos(data),
      totalIngresos: baseImponible.totalIngresos(data),
      totalEgresosGiro: baseImponible.totalEgresosGiro(data),
      totalGastosRechazados: baseImponible.totalGastosRechazados(data),
      baseImponibleAntesIncentivo: baseImponible.baseImponibleAntesIncentivo(data),
      biInvertida: baseImponible.biInvertida(data),
      incentivoAhorro: baseImponible.incentivoAhorro(data),
      baseImponibleIDPC: baseImponible.baseImponibleIDPC(data),
      idpc: baseImponible.idpc(data),
      reajustePPM: baseImponible.reajustePPM(data),
      ppmActualizado: baseImponible.ppmActualizado(data),
      resultadoLiquidacionAnual: baseImponible.resultadoLiquidacionAnual(data),
    },
    cptSimplificado: {
      cptsPositivoFinal: cptSimplificado.cptsPositivoFinal(data),
    },
    raiFinal: {
      rai: raiFinal.rai(data),
    },
    rtre: {
      subtotalAntes: rtreFormulas.subtotalAntes(data),
      remanenteSiguiente: rtreFormulas.remanenteSiguiente(data),
    },
    r17: {
      totalIngresos: r17.totalIngresos(data),
      totalEgresos: r17.totalEgresos(data),
      biAntesIncentivo: r17.biAntesIncentivo(data),
      biAfectaIDPC: r17.biAfectaIDPC(data),
    },
    r18: { rai: r18.rai(data) },
    r19: { cptsPositivoFinal: r19.cptsPositivoFinal(data) },
  };
};

module.exports = {
  antecedentes,
  creditoIPE,
  baseImponible,
  cptSimplificado,
  raiFinal,
  rtreFormulas,
  r17,
  r18,
  r19,
  r20,
  r21,
  recalculate,
};
